import clsx from 'clsx'
import { ArrowRight, Trash2 } from 'lucide-react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, Button, ButtonGroup, FileList, FileUpload, IconButton, useBreakpoint } from 'slab'
import { type Document } from 'utils/web_api.schemas'

export interface UploadError {
  type?: 'info' | 'notice' | 'success' | 'caution' | 'warning' | 'critical' | 'emphasis'
  title: string
  errors?: string[]
}

interface QuoteRequestUploadProps {
  title: string
  files: Document[]
  accept?: string
  requiredFormat?: string
  multiple?: boolean
  error?: UploadError
  isUploading?: boolean
  onUpload?: (files: File[]) => void
  onDelete?: (file: Document) => void
  onBack?: () => void
  onNext?: () => void
  canProceed?: boolean
  children?: React.ReactNode
}

export function QuoteRequestUploadPage ({
  title,
  files,
  accept,
  requiredFormat,
  multiple = false,
  error,
  isUploading = false,
  onUpload = () => {},
  onDelete = () => {},
  onBack = () => {},
  onNext = () => {},
  canProceed = true,
  children
}: QuoteRequestUploadProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const errors = error?.errors ?? []

  const isColumnView = breakpointIsAtLeast('lg')

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      return
    }

    onUpload([...event.target.files])

    // Allows uploading the same file again (in case of delete)
    event.target.value = ''
  }

  return (
    <div className={clsx('gap-8 max-w-[880px] mx-auto', {
      'grid grid-cols-2': isColumnView,
      'flex flex-col': !isColumnView
    }
    )}
    >
      <div>
        <h1 className='text-2xl font-medium text-gray-800 mb-10'>{title}</h1>
        {children}
      </div>
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-4'>
          <div className='grid h-[320px]'>
            <FileUpload
              height='full'
              multiple={multiple}
              accept={accept}
              onChange={handleUpload}
              disabled={isUploading}
            >
              <FileUpload.Content
                icon={isUploading ? null : undefined /* default */}
                instructions={isUploading ? <></> : undefined /* default */}
                details={isUploading
                  ? <LoadingSpinnerCentered />
                  : (
                    <ul className='caption text-center'>
                      <li>20 MB max size</li>
                      {requiredFormat && <li>{requiredFormat}</li>}
                    </ul>)}
              />
            </FileUpload>
          </div>

          {error &&
            <Banner
              title={error.title}
              type={error.type ?? 'critical'}
            >
              {errors.length > 1
                ? (
                  <ul className='list-disc list-inside'>
                    {errors.map(err => <li key={err}>{err}</li>)}
                  </ul>)
                : errors[0]}
            </Banner>}
        </div>

        {files.length > 0 &&
          <div className='flex flex-col gap-2'>
            <p className='label-default text-ds-text-secondary'>
              {`${files.length} file${files.length === 1 ? '' : 's'} uploaded`}
            </p>

            <FileList>
              {files.map(file => (
                <FileList.File
                  key={file.id}
                  name={file.filename}
                  size={file.byte_size}
                  status='success'
                  actions={
                    <IconButton label='Delete' icon={Trash2} onClick={() => onDelete(file)} />
                  }
                />))}
            </FileList>
          </div>}

        <ButtonGroup align='right'>
          <Button
            size='large'
            variant='secondary'
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            size='large'
            variant={canProceed ? 'primary' : 'secondary'}
            type='submit'
            disabled={isUploading}
            rightIcon={ArrowRight}
            onClick={onNext}
          >
            Next
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}
